import { http } from './request';
import { IM_SESSION_URLS as API_URLS } from './urls';
import type {
  ImSessionLeadStatus,
  ImSessionReplyTaskStatus,
} from '../../common/enums/imSessionStatus';
import type { CategoryCollectTask } from './categories';

export interface FetchImSessionsParams {
  ecomAccountId: string;
  leadStatus?: ImSessionLeadStatus;
  page?: number;
  size?: number;
}

export interface SubmitPrivateLeadCollectPayload {
  shopId: string;
  ecomAccountId: string;
  accountName?: string;
  topConversations?: number;
  saveOutput?: boolean;
}

export interface SubmitPrivateLeadCollectResponse {
  message: string;
  ecomAccountId: string;
  tasks?: CategoryCollectTask[];
}

export interface ImSessionItem {
  id?: number;
  imSessionId: string;
  ecomAccountId?: string;
  platform?: string;
  platformSessionId?: string;
  sessionName?: string;
  conversationType?: string;
  /** 客户 stable_user_id，抖音下对应 sec_uid */
  customerStableUserId?: string;
  /** @deprecated 旧字段名，请使用 customerStableUserId */
  customerSecUid?: string;
  customerName?: string;
  customerAvatarUrl?: string;
  /** 客户 public_user_id，抖音下对应抖音号 */
  customerPublicUserId?: string;
  /** @deprecated 旧字段名，请使用 customerPublicUserId */
  customerChatUid?: string;
  inboundMessageCount?: number;
  timelineMessageCount?: number;
  hasReplyHistory?: boolean;
  hasNewIncomingIntent?: boolean;
  latestMessageText?: string;
  lastMessageTime?: string | null;
  leadStatus: ImSessionLeadStatus;
  followed?: boolean;
  replyTaskStatus?: ImSessionReplyTaskStatus;
  replyTaskErrorMessage?: string;
  replyTaskStartedAt?: string | number | null;
  replyTaskFinishedAt?: string | number | null;
  // 以下字段为旧版兼容保留，新接口中不再返回
  leadKey?: string;
  actorName?: string;
  actorPublicUserId?: string;
  actorAvatar?: string;
  actorProfileUrl?: string;
  contentText?: string;
  sourceTitle?: string;
  leadUrl?: string;
  ecomAccountName?: string;
}

export interface ImSessionListResponse {
  total: number;
  list: ImSessionItem[];
}

export interface PrivateLeadItem {
  leadType: 'IM' | 'COMMENT';
  leadTypeLabel?: string;
  leadId: string;
  ecomAccountId?: string;
  ecomAccountName?: string;
  leadStatus: ImSessionLeadStatus;
  replyContent?: ReplyContent | null;
  replyDisplayText?: string | null;
  replySendText?: string | null;
  // IM 专属字段（imSessionId === leadId）
  imSessionId?: string;
  platform?: string;
  platformSessionId?: string;
  sessionName?: string;
  customerName?: string;
  customerAvatarUrl?: string;
  customerStableUserId?: string;
  /** @deprecated 请使用 customerStableUserId */
  customerSecUid?: string;
  customerPublicUserId?: string;
  /** @deprecated 请使用 customerPublicUserId */
  customerChatUid?: string;
  inboundMessageCount?: number;
  timelineMessageCount?: number;
  hasReplyHistory?: boolean;
  hasNewIncomingIntent?: boolean;
  latestMessageText?: string;
  lastMessageTime?: string | null;
  followed?: boolean;
  replyTaskStatus?: ImSessionReplyTaskStatus;
  replyTaskErrorMessage?: string;
  // COMMENT 专属字段
  id?: number;
  actorName?: string;
  actorPublicUserId?: string;
  actorAvatar?: string;
  actorProfileUrl?: string;
  contentText?: string;
  sourceTitle?: string;
  sourceContentPlatformId?: string;
  sourceActorName?: string;
  sourceActorAvatar?: string;
  sourceActorProfileUrl?: string;
  leadUrl?: string;
}

export interface FetchPrivateLeadsParams {
  ecomAccountId?: string;
  leadType?: 'IM' | 'COMMENT';
  keyword?: string;
  leadStatus?: ImSessionLeadStatus;
  page?: number;
  size?: number;
}

export interface PrivateLeadListResponse {
  total: number;
  list: PrivateLeadItem[];
}

export interface FetchImSessionMessagesParams {
  imSessionId: string;
  limit?: number;
}

export type ImSessionMessageDirection = 'INBOUND' | 'OUTBOUND' | string;

export interface ImSessionMessageItem {
  imMessageId: string;
  timelineType?: 'INBOUND_INTENT' | 'FOLLOW_REPLY' | string;
  senderName?: string;
  /** 发送方 stable_user_id，抖音下对应 sec_uid */
  senderStableUserId?: string;
  /** @deprecated 旧字段名，请使用 senderStableUserId */
  senderPlatformId?: string;
  contentText?: string;
  direction?: ImSessionMessageDirection;
  followStatus?: string;
  sendTime?: string | null;
}

export interface ImSessionMessagesResponse {
  imSessionId: string;
  list: ImSessionMessageItem[];
}

export interface ReplyImSessionPayload {
  imSessionId: string;
  message: string;
}

export interface ReplyImSessionResponse {
  imSessionId: string;
  replyTaskStatus: ImSessionReplyTaskStatus;
  message?: string;
}

export interface UpdateImSessionStatusPayload {
  imSessionId: string;
  leadStatus: ImSessionLeadStatus;
  failureReason?: string;
}

export interface UpdateImSessionStatusResponse {
  imSessionId: string;
  leadStatus: ImSessionLeadStatus;
  replyTaskStatus?: ImSessionReplyTaskStatus;
  replyTaskErrorMessage?: string;
}

export interface ReplyContentText {
  type: 'TEXT';
  text: string;
}

export interface ReplyContentRichTextPart {
  type: 'TEXT' | 'SLOT';
  text?: string;
  slotType?: 'PRODUCT_LINK';
  displayText?: string;
  sendText?: string;
  extra?: {
    productId: string;
    platformType: string;
    platformProductId: string;
  };
}

export interface ReplyContentRichText {
  type: 'RICH_TEXT';
  parts: ReplyContentRichTextPart[];
}

export type ReplyContent = ReplyContentText | ReplyContentRichText;

export type PrivateLeadType = 'IM' | 'COMMENT';
export type PrivateLeadStatus = 'UNFOLLOWED' | 'IN_PROGRESS' | 'FOLLOWED' | 'FAILED';

export interface UpdatePrivateLeadStatusPayload {
  leadType: PrivateLeadType;
  leadId: string;
  ecomAccountId?: string;
  leadStatus: PrivateLeadStatus;
  failureReason?: string;
  replyContent?: ReplyContent | null;
}

export interface UpdatePrivateLeadStatusResponse {
  leadType: string;
  leadId: string;
  leadStatus: string;
}

/**
 * 私信会话列表
 */
export const fetchImSessions = async (params: FetchImSessionsParams) => {
  const query: FetchImSessionsParams = {
    ecomAccountId: params.ecomAccountId,
    page: params.page ?? 1,
    size: params.size ?? 10,
  };

  if (params.leadStatus) {
    query.leadStatus = params.leadStatus;
  }

  return http.get<ImSessionListResponse>(API_URLS.imSessions, { params: query });
};

/**
 * 私信会话消息明细
 */
export const fetchImSessionMessages = async (params: FetchImSessionMessagesParams) => {
  const query: FetchImSessionMessagesParams = {
    imSessionId: params.imSessionId,
    limit: params.limit ?? 20,
  };

  return http.get<ImSessionMessagesResponse>(API_URLS.imSessionMessages, { params: query });
};

/**
 * 发起私信会话跟进任务
 */
export const replyImSession = (payload: ReplyImSessionPayload) => {
  return http.post<ReplyImSessionResponse>(API_URLS.imSessionsReply, payload);
};

/**
 * 手动回写私信会话私信发送状态
 */
export const updateImSessionStatus = (payload: UpdateImSessionStatusPayload) => {
  return http.post<UpdateImSessionStatusResponse>(API_URLS.imSessionsStatus, payload);
};

/**
 * 私域线索列表（合并私信会话 + 评论区线索）
 */
export const fetchPrivateLeads = async (params: FetchPrivateLeadsParams) => {
  const query: FetchPrivateLeadsParams = {
    page: params.page ?? 1,
    size: params.size ?? 10,
  };

  if (params.ecomAccountId) {
    query.ecomAccountId = params.ecomAccountId;
  }
  if (params.leadType) {
    query.leadType = params.leadType;
  }
  if (params.keyword) {
    query.keyword = params.keyword;
  }
  if (params.leadStatus) {
    query.leadStatus = params.leadStatus;
  }

  return http.get<PrivateLeadListResponse>(API_URLS.privateLeads, { params: query });
};

/**
 * 更新私域线索跟进状态
 */
export const updatePrivateLeadStatus = (payload: UpdatePrivateLeadStatusPayload) => {
  return http.post<UpdatePrivateLeadStatusResponse>(API_URLS.privateLeadsStatus, payload);
};

export const submitPrivateLeadCollect = (payload: SubmitPrivateLeadCollectPayload) => {
  return http.post<SubmitPrivateLeadCollectResponse>(API_URLS.imSessionsCollect, payload);
};

export interface GeneratePrivateLeadSuggestionsPayload {
  shopId?: string;
  session: {
    sessionKey: string;
    imSessionId?: string;
    leadId?: string;
    actorName?: string;
    contentText?: string;
    sourceTitle?: string;
    leadStatus?: string;
    productId?: string;
    platformProductId?: string;
    productName?: string;
    productUrl?: string;
  };
}

export interface GeneratePrivateLeadSuggestionsResponse {
  result: {
    replyText: string;
    hasAssociatedProduct?: boolean;
    associatedProduct?: {
      productId?: string;
      platformProductId?: string;
      productName?: string;
      productUrl?: string;
    } | null;
  };
}

export const generatePrivateLeadSuggestions = (payload: GeneratePrivateLeadSuggestionsPayload) => {
  return http.post<GeneratePrivateLeadSuggestionsResponse>(API_URLS.privateLeadsSuggestions, payload, { timeout: 300000 });
};
